import {
  UserDto,
  UserMemberStatusDto,
} from '@act/shared/data-transfer-objects';
import { ColumnConfiguration, MemberStatusTag } from '@act/storybook';
import { Box, Typography } from '@mui/material';
import { AddressFormatter } from 'utilities/formatters/address-formatter';
import { Fragment } from 'react/jsx-runtime';
import { timeFormatter } from '@act/shared/utilities';
import { displayName } from 'react-components/docket/components/utilities/displayNameAndBirthdayCheck.utils';

const getStatusColor = (status: UserMemberStatusDto) => {
  if (!status || !status.name) {
    return 'error';
  }

  switch (status?.key) {
    case 'queued':
    case 're-engaged':
      return 'success';
    case 'attempted':
    default:
      return 'neutral';
  }
};

const getColumnConfigurations = (
  isMobile: boolean
): (ColumnConfiguration<UserDto> & {
  width?: string;
  align?: 'left' | 'center' | 'right';
})[] => [
  {
    id: 'name',
    value: (props: UserDto): React.ReactNode => {
      return <Typography variant="bodyLarge">{displayName(props)}</Typography>;
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Member',
    ...(isMobile && { width: '150px' }), // Fixed width for member name only on mobile
  },
  {
    id: 'addresses',
    value: (props: UserDto): React.ReactNode => {
      if (!props?.addresses?.length) {
        return null;
      }
      return (
        <Typography
          variant="bodyLarge"
          sx={isMobile ? { wordBreak: 'break-word' } : {}}
        >
          {AddressFormatter.toShort(props?.addresses[0])}
        </Typography>
      );
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: false,
    title: 'Location',
    ...(isMobile && { width: '100px' }), // Reduced width by half, allows word wrapping only on mobile
  },
  {
    id: 'memberStatus',
    value: (props: UserDto): React.ReactNode => {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            gap: 1,
          }}
        >
          <MemberStatusTag status={props?.memberStatus} />
        </Box>
      );
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Status',
    ...(isMobile && { width: '120px' }), // Fixed width for status only on mobile
    ...(isMobile && { align: 'center' }), // Center align status column header and content only on mobile
  },
  {
    id: 'lastEventAt',
    value: (props: UserDto): React.ReactNode => {
      if (!props.lastEventType) {
        return null;
      }
      return (
        <Fragment>
          <Typography variant="bodyLarge">
            {timeFormatter.epochToShortDate(props?.lastEventAt)}
            <br />
            {props?.lastEventType}
          </Typography>
        </Fragment>
      );
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Last Event',
    ...(isMobile && { width: '140px' }), // Fixed width for last event only on mobile
  },
  {
    id: 'assignedGuide',
    value: (props: UserDto): React.ReactNode => {
      return (
        <Typography variant="bodyLarge">
          {props?.assignedGuide || 'Unassigned'}
        </Typography>
      );
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Guide',
    ...(isMobile && { width: '120px' }), // Fixed width for guide only on mobile
  },
  {
    id: 'standardMemberAttributes.outreachPhase' as keyof UserDto,
    value: (props: UserDto): React.ReactNode => {
      return (
        <Typography variant="bodyLarge">
          {props?.standardMemberAttributes?.outreachPhase}
        </Typography>
      );
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Phase',
    ...(isMobile && { width: '100px' }), // Fixed width for phase only on mobile
  },
  {
    id: 'monthlyOutreach',
    value: (props: UserDto): React.ReactNode => {
      return (
        <Typography variant="bodyLarge">{props?.monthlyOutreach}</Typography>
      );
    },
    isNumeric: true,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Outreach',
    ...(isMobile && { width: '100px' }), // Fixed width for outreach only on mobile
  },
];

// Export the function that returns the configurations
export const MEMBER_TABLE_COLUMN_CONFIGURATIONS = (isMobile: boolean) =>
  getColumnConfigurations(isMobile);
